/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { Button, Grid, Spacer, Spinner, TextGroupMedium } from "@cd2/components"
import format from "date-fns/format"
import { IRecord } from "../../../types/schema"
import { doo } from "@cd2/tools/build/es6"
import { Flex } from "../../../interfaceParts/Flex"
import { Pill } from "../../../interfaceParts/Pill"
import { listFlowTags } from "./listFlowTags"
import { useAsyncEffect } from "../../../hooks/useAsyncEffect"
import { useSkeem } from "../../../providers/SkeemProvider"
import { assert } from "../../../utils/assert"
import React, { useState } from "react"
import { Frame } from "../../../interfaceParts/Frame"
import { DelayedRender } from "../../../interfaceParts/DelayedRender"
import { LabelInfo } from "../../../interfaceParts/LabelInfo"
import { HoverUserLoader } from "../../../interfaceParts/hoverLoaders/HoverUserLoader"
import { HoverEmployeeLoader } from "../../../interfaceParts/hoverLoaders/HoverEmployeeLoader"
import { icons } from "../../../utils/icons"
import { useAsyncCallback } from "../../../hooks/useAsyncCallback"
import { useReloadKey } from "../../../hooks/useReloadKey"
import { useDialogueConfirmation2 } from "../../../hooks/useDialogConfirmation"
import { H2, P } from "@cd2/text"
import { CancelButton } from "../../../interfaceParts/CancelButton"
import { Fields } from "@cd2/forms"
import { useGlobalState } from "../../../utils/globalState"
import {
  concernOptions,
  getColorForConcern,
  ResultConcernSelectField,
} from "../../../interfaceParts/ResultConcernSelectField"
import { useEvents } from "../../../utils/useEvents"
import { useMutatorState } from "../../../hooks/useMutatorState"
import { buildSearchFilter } from "../../../utils/buildSearchFilter"
import {
  canChangeCaseCompany,
  useChangeCaseCompanyDialog,
} from "../../../hooks/useChangeCaseCompanyDialog"
import { updateResultRag } from "../../../utils/updateResultRag"

type FlowResult = IRecord<
  "flowResults",
  | "flowName"
  | "identifier"
  | "name"
  | "createdAt"
  | "questionCache"
  | "hrLastViewedAt"
  | "user"
  | "invitedUsers"
  | "hrLastViewedBy"
  | "employees"
  | "isEmployee"
  | "concern"
> & {
  company: IRecord<"companies", "canSeeHrTeamsArea" | "name">
}
type Props = {
  flowResultId: string
  reloadFlowResults(): void
}

export function AssessmentDetailsTab({ flowResultId, reloadFlowResults }: Props) {
  const skeem = useSkeem()
  const [reloadKey, triggerReload] = useReloadKey()
  const globalState = useGlobalState()
  const employeesEnabled = !globalState.settings.disableEmployeeFeatures

  const [flowResult, setFlowResult] = useState<FlowResult | null>(null)

  useAsyncEffect(async () => {
    const record = await skeem.fetch2<FlowResult | null>({
      first: {
        name: "flowResults",
        attributes: [
          "flowName",
          "identifier",
          "name",
          "createdAt",
          "questionCache",
          "hrLastViewedAt",
          "hrLastViewedBy",
          "user",
          "invitedUsers",
          "employees",
          "isEmployee",
          "concern",
          { name: "company", attributes: ["name", "canSeeHrTeamsArea"] },
        ],
        matches: { id: flowResultId },
      },
    })
    assert(record, "Record not found")
    assert(record.company, "Company not found")
    setFlowResult(record)
  }, [flowResultId, reloadKey])

  const triggerEditEmployees = useEditEmployees()
  const handleEditEmployees = useAsyncCallback(async () => {
    if (!flowResult) return
    const shouldReload = await triggerEditEmployees({
      resultId: flowResultId,
      companyId: flowResult.company.id,
    })
    if (shouldReload) {
      triggerReload()
      reloadFlowResults()
    }
  })

  const triggerChangeCompany = useChangeCaseCompanyDialog()
  const handleChangeCompany = useAsyncCallback(async () => {
    if (!flowResult?.company) return
    const shouldReload = await triggerChangeCompany({
      resultId: flowResultId,
      companyId: flowResult.company.id,
      companyName: flowResult.company.name,
    })
    if (shouldReload) {
      triggerReload()
      reloadFlowResults()
    }
  })

  const triggerEditConcern = useEditResultConcernDialog()
  const handleEditConcern = useAsyncCallback(async () => {
    const shouldReload = await triggerEditConcern({
      resultId: flowResultId,
    })
    if (shouldReload) {
      triggerReload()
      reloadFlowResults()
    }
  })

  if (!flowResult) {
    return <LoadingFrame />
  }

  const concernOption = concernOptions.find(option => option.value === flowResult.concern)

  return (
    <Grid gap={16}>
      {canChangeCaseCompany(globalState) && (
        <LabelInfo
          label="Company"
          value={
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 4px;
              `}
            >
              {flowResult.company.name}
              <Button
                leftIcon={icons.edit}
                extendTheme={{ padding: 8 }}
                buttonStyle="text"
                onClick={handleChangeCompany}
              >
                Edit
              </Button>
            </div>
          }
        />
      )}
      <LabelInfo label="Case coaching type" value={flowResult.flowName} />
      <LabelInfo label="Identifier" value={flowResult.identifier} />
      <LabelInfo
        label="Concern"
        value={
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 4px;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 4px;
              `}
            >
              <div
                css={css`
                  width: 20px;
                  height: 20px;
                  background: ${getColorForConcern(flowResult.concern)};
                  border-radius: 30%;
                `}
              />
              {flowResult.concern ? concernOption?.label ?? flowResult.concern : "-"}
            </div>
            {!globalState.user.isReadonly && (
              <Button
                leftIcon={icons.edit}
                extendTheme={{ padding: 8 }}
                buttonStyle="text"
                onClick={handleEditConcern}
              >
                Edit
              </Button>
            )}
          </div>
        }
      />
      <LabelInfo
        label="Name"
        value={
          flowResult.name || (
            <i
              css={css`
                color: #999;
              `}
            >
              No name set
            </i>
          )
        }
      />
      {!flowResult.isEmployee && (
        <React.Fragment>
          <LabelInfo
            label="Taken by"
            value={flowResult.user ? <HoverUserLoader id={flowResult.user.id} /> : null}
          />
          <LabelInfo
            label="Additional assessors"
            value={
              flowResult.invitedUsers.length > 0
                ? flowResult.invitedUsers.map(user => (
                    <HoverUserLoader key={user.id} id={user.id} />
                  ))
                : null
            }
          />
          {flowResult.createdAt && (
            <LabelInfo
              label="Date Started"
              value={format(new Date(flowResult.createdAt), "k:mm dd/MM/yyyy")}
            />
          )}
          <LabelInfo
            label="Flow Tags"
            value={doo(() => {
              const tags = listFlowTags(flowResult.questionCache.steps)

              if (tags.length === 0) {
                return "-"
              } else {
                return (
                  <Flex gap={4} wrap>
                    {tags.map(tag => (
                      <Pill key={tag}>{tag}</Pill>
                    ))}
                  </Flex>
                )
              }
            })}
            help={`As you answer the questions, we will automatically add and remove tags based on your responses. These tags are used to indicate different situations that may require further attention or action.`}
          />
          {flowResult.company.canSeeHrTeamsArea && (
            <React.Fragment>
              <LabelInfo
                label="Last Viewed By HR At"
                value={
                  flowResult.hrLastViewedAt
                    ? format(new Date(flowResult.hrLastViewedAt), "k:mm dd/MM/yyyy")
                    : null
                }
              />
              <LabelInfo
                label="Last Viewed By HR"
                value={
                  flowResult.hrLastViewedBy ? (
                    <HoverUserLoader id={flowResult.hrLastViewedBy?.id} />
                  ) : null
                }
              />
            </React.Fragment>
          )}
          {employeesEnabled && (
            <LabelInfo
              label="Employees"
              value={
                <div>
                  <Grid gap={4}>
                    {flowResult.employees.map(employee => (
                      <HoverEmployeeLoader key={employee.id} id={employee.id} />
                    ))}
                  </Grid>
                  {!globalState.user.isReadonly && (
                    <Button leftIcon={icons.edit} onClick={handleEditEmployees}>
                      Edit
                    </Button>
                  )}
                </div>
              }
            />
          )}
        </React.Fragment>
      )}
    </Grid>
  )
}

export function LoadingFrame({ height = 300 }: { height?: number }) {
  return (
    <Frame minHeight={height}>
      <DelayedRender>
        <Spinner color="black" />
      </DelayedRender>
    </Frame>
  )
}

export function useEditEmployees() {
  return useDialogueConfirmation2<boolean, { companyId: string; resultId: string }>({
    Component({ dialogData, onResolve, registerOutsideClick }) {
      registerOutsideClick(() => onResolve(false))

      type Employee = IRecord<"employees", "firstName" | "lastName" | "email" | "notes">

      const skeem = useSkeem()
      const [employees, setEmployees] = useState<Employee[] | null>(null)
      const [value, setValue] = useState<string[]>([])

      useAsyncEffect(async () => {
        setEmployees([])
        const employees = await skeem.fetch2<Employee[]>({
          model: {
            name: "employees",
            attributes: ["firstName", "lastName", "email", "notes"],
            filter: {
              and: [
                { matches: { archived: false } },
                {
                  or: [
                    { matches: { company: dialogData.companyId } },
                    {
                      exists: {
                        path: [
                          "user",
                          { name: "accessibleCompanies", matches: { id: dialogData.companyId } },
                        ],
                      },
                    },
                  ],
                },
              ],
            },
            sort: { by: "firstName", direction: "asc" },
          },
        })
        console.log("EMPLOYEES", employees)
        setEmployees(employees)
      }, [dialogData.companyId])

      useAsyncEffect(async () => {
        const employees = await skeem.fetch2<IRecord<"employees">[]>({
          model: {
            name: "employees",
            matches: { flowResults: { some: { id: dialogData.resultId } } },
          },
        })

        setValue(employees.map(employee => employee.id))
      }, [])

      const handleSubmit = useAsyncCallback(async () => {
        await skeem.mutate({
          flowResults: {
            update: {
              id: dialogData.resultId,
              employees: { set: value },
            },
          },
        })
        onResolve(true)
      })

      if (!employees) {
        return <LoadingFrame />
      }

      return (
        <div>
          <H2>Edit Employees</H2>
          <P>Change the employees associated with this assessment.</P>
          <MultiEmployeeSelectField
            value={value}
            onChange={opts => setValue(opts.map(opt => opt.id))}
            companyId={dialogData.companyId}
            label="Employees"
          />
          <Spacer />
          <Flex gap={4}>
            <Button leftIcon={icons.save} onClick={handleSubmit}>
              Save
            </Button>
            <CancelButton onClick={() => onResolve(false)} />
          </Flex>
        </div>
      )
    },
  })
}

type Employee = IRecord<"employees", "firstName" | "lastName" | "email" | "notes"> & {
  location: IRecord<"locations", "name"> | null
}

export function MultiEmployeeSelectField({
  value,
  onChange,
  companyId,
  label,
  error,
  singleSelect,
  showRequiredStar,
}: {
  value: string[]
  onChange: (opts: Employee[]) => void
  companyId: string
  label?: string
  error?: string
  singleSelect?: boolean
  showRequiredStar?: boolean
}) {
  const globalState = useGlobalState()
  const skeem = useSkeem()
  const [valueRecords, setValueRecords] = useState<Employee[]>([])

  useAsyncEffect(
    async ({ isAlive }) => {
      if (value.length === 0) {
        setValueRecords([])
        return
      }

      const currentIds = valueRecords.map(employee => employee.id)
      const missingIds = value.filter(id => !currentIds.includes(id))

      if (missingIds.length > 0) {
        const records = await skeem.fetch2<Employee[]>({
          model: {
            name: "employees",
            attributes: [
              "firstName",
              "lastName",
              "email",
              "notes",
              { name: "location", attributes: ["name"] },
            ],
            matches: { id: { or: missingIds } },
          },
        })
        if (!isAlive()) {
          return
        }

        setValueRecords(prev => {
          const existing = prev.filter(e => value.includes(e.id))
          return [...existing, ...records]
        })
      } else {
        setValueRecords(prev => prev.filter(employee => value.includes(employee.id)))
      }
    },
    [JSON.stringify(value)],
  )

  async function loadEmployees(query: string) {
    const filterAnd: any[] = [
      { matches: { archived: false } },
      globalState.permissions.employeeVisibility === "team"
        ? {
            or: [
              {
                matches: {
                  teams: { some: { leaders: { some: { id: { session: true } } } } },
                },
              },
              {
                matches: {
                  teams: { some: { members: { some: { id: { session: true } } } } },
                },
              },
            ],
          }
        : undefined,
      {
        or: [
          { matches: { company: companyId } },
          {
            exists: {
              path: ["user", { name: "accessibleCompanies", matches: { id: companyId } }],
            },
          },
        ],
      },
    ].filter(Boolean)

    filterAnd.push(buildSearchFilter(["firstName", "lastName", "email"], query))

    return skeem.fetch2<Employee[]>({
      model: {
        name: "employees",
        attributes: [
          "firstName",
          "lastName",
          "email",
          "notes",
          { name: "location", attributes: ["name"] },
        ],
        filter: { and: filterAnd },
        sort: { by: "firstName", direction: "asc" },
        paginate: { size: 50 },
      },
    })
  }

  function handleChange(selected: Employee | Employee[] | null) {
    const employees = singleSelect
      ? selected
        ? [selected as Employee]
        : []
      : (selected as Employee[] | null) ?? []
    setValueRecords(employees)
    onChange(employees)
  }

  return (
    <Fields.AsyncSelect
      label={
        showRequiredStar ? (
          <span>
            {label ?? "Employees"}{" "}
            <span
              css={css`
                color: red;
              `}
            >
              *
            </span>
          </span>
        ) : (
          label ?? "Employees"
        )
      }
      loadOptions={loadEmployees}
      getOptionLabel={employee => `${employee.firstName} ${employee.lastName}`}
      getOptionValue={employee => employee.id}
      value={singleSelect ? valueRecords[0] ?? null : valueRecords}
      onChange={handleChange}
      error={error}
      isMulti={!singleSelect}
      defaultOptions
    />
  )
}

function useEditResultConcernDialog() {
  return useDialogueConfirmation2<boolean, { resultId: string }>({
    Component({ onResolve, dialogData, registerOutsideClick }) {
      registerOutsideClick(() => onResolve(false))
      const events = useEvents()

      const [state, mutate] = useMutatorState({
        loaded: false,
        concernWas: "" as string,
        concern: "" as string,
      })

      useAsyncEffect(async () => {
        const result = await window.skeem.fetch2<IRecord<"flowResults", "concern"> | null>({
          first: {
            name: "flowResults",
            attributes: ["concern"],
            matches: { id: dialogData.resultId },
          },
        })
        assert(result, "Result not found")
        mutate.setPartial({
          loaded: true,
          concernWas: result.concern,
          concern: result.concern,
        })
      }, [])

      const handleSubmit = useAsyncCallback(async () => {
        if (state.concern === state.concernWas) {
          onResolve(false)
          return
        }

        await updateResultRag({
          resultId: dialogData.resultId,
          newConcern: state.concern,
          oldConcern: state.concernWas,
        })

        await events.updateRecord("results", dialogData.resultId, {
          concern: { was: state.concernWas, now: state.concern },
        })
        onResolve(true)
      })

      if (!state.loaded) return <LoadingFrame />

      return (
        <div>
          <TextGroupMedium
            heading="Edit Concern"
            body="Change the concern associated with this assessment."
          />
          <ResultConcernSelectField
            value={state.concern}
            onChange={value => mutate.setProp("concern", value)}
          />
          <Spacer size={96} />
          <Flex gap={4}>
            <Button leftIcon={icons.save} onClick={handleSubmit}>
              Save
            </Button>
            <CancelButton onClick={() => onResolve(false)} />
          </Flex>
        </div>
      )
    },
  })
}
